import { useEffect, useRef } from 'react';

import type { FullPageEditorPresetAPI } from '@confluence/editor-presets/entry-points/useFullPageEditorPreset.ts';
import { useEditorRovoButtonActions } from '@confluence/edit-page-rovo-config-provider/entry-points/useEditorRovoButtonActions.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { getLogger } from '@confluence/logger';

import type { EditorInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/useSharedPluginStateSelector';

/**
 * Reads agent mention state from the mentions plugin via SharedPluginState and writes it to
 * EditorRovoButtonActionsStore. Also resolves the raw AAID → Agent Studio UUID via the
 * assistance API so that downstream consumers can open Rovo chat with the correct agent.
 *
 * Gated behind the `platform_editor_agent_mentions` experiment gate.
 */
export const useAgentMentionBridge = (
	fullPageEditorPresetAPI: FullPageEditorPresetAPI | undefined,
): void => {
	const agentMentionId: string | null =
		useSharedPluginStateSelector(
			fullPageEditorPresetAPI as EditorInjectionAPI<any, any>,
			'mention.lastInsertedAgentMentionId',
		) ?? null;
	const agentMentionContext: string | null =
		useSharedPluginStateSelector(
			fullPageEditorPresetAPI as EditorInjectionAPI<any, any>,
			'mention.lastInsertedAgentMentionContext',
		) ?? null;
	const agentMentionName: string | null =
		useSharedPluginStateSelector(
			fullPageEditorPresetAPI as EditorInjectionAPI<any, any>,
			'mention.lastInsertedAgentMentionName',
		) ?? null;
	const agentMentionParentNodeType: string | null =
		useSharedPluginStateSelector(
			fullPageEditorPresetAPI as EditorInjectionAPI<any, any>,
			'mention.lastInsertedAgentMentionParentNodeType',
		) ?? null;
	const agentMentionInsertionCount: number =
		useSharedPluginStateSelector(
			fullPageEditorPresetAPI as EditorInjectionAPI<any, any>,
			'mention.lastAgentMentionInsertionCount',
		) ?? 0;

	// Use refs so payload values can be read inside the effect without being reactive deps.
	// agentMentionContext changes on every keystroke; including it in the dep array would
	// trigger a new fetch() on every character typed after the agent mention is inserted.
	const agentMentionContextRef = useRef(agentMentionContext);
	agentMentionContextRef.current = agentMentionContext;
	const agentMentionNameRef = useRef(agentMentionName);
	agentMentionNameRef.current = agentMentionName;
	const agentMentionParentNodeTypeRef = useRef(agentMentionParentNodeType);
	agentMentionParentNodeTypeRef.current = agentMentionParentNodeType;

	const { setAgentMentionState, setResolvedAgentMentionId } = useEditorRovoButtonActions();

	// expValEquals is intentionally inlined (not stored in a variable) to satisfy the
	// confluence-feature-gating/inline-usage lint rule, and intentionally omitted from
	// the dep array (static gate value — not reactive) to satisfy exhaustive-deps.
	useEffect(() => {
		if (!expValEquals('platform_editor_agent_mentions', 'isEnabled', true)) {
			return;
		}

		setAgentMentionState({
			agentMentionId,
			agentMentionInsertionCount,
			agentMentionContext: agentMentionContextRef.current,
			agentMentionName: agentMentionNameRef.current,
			agentMentionParentNodeType: agentMentionParentNodeTypeRef.current,
		});

		if (!agentMentionId) {
			setResolvedAgentMentionId(null);
			return;
		}

		// Resolve the AAID (from ADF mention node) to the Rovo Agent Studio UUID.
		// The ADF mention node's `id` is the Atlassian Account ID, but Rovo chat's
		// `agentId` expects the Agent Studio UUID. We resolve via the assistance API.
		let isCancelled = false;
		const controller = new AbortController();
		void fetch(
			`/gateway/api/assist/rovo/v1/agents/accountid/${encodeURIComponent(agentMentionId)}`,
			{
				method: 'GET',
				credentials: 'include',
				headers: {
					'x-product': 'confluence',
					'x-experience-id': 'ai-mate',
				},
				signal: controller.signal,
			},
		)
			.then((res) => {
				if (!res.ok) throw new Error(res.statusText);
				return res.json();
			})
			.then((agent: { id?: string }) => {
				// Guard against a stale .then() microtask resolving after the effect cleaned up.
				if (!isCancelled) {
					setResolvedAgentMentionId(agent?.id ?? null);
				}
			})
			.catch((err: unknown) => {
				// Ignore AbortError — thrown when the controller aborts on cleanup/unmount.
				if (err instanceof Error && err.name === 'AbortError') return;
				getLogger('useAgentMentionBridge').error`Failed to resolve agent UUID: ${err}`;
				if (!isCancelled) {
					setResolvedAgentMentionId(null);
				}
			});

		return () => {
			isCancelled = true;
			controller.abort();
		};
	}, [
		agentMentionId,
		agentMentionInsertionCount,
		// agentMentionContext, agentMentionName and agentMentionParentNodeType intentionally omitted —
		// read via refs above to avoid re-fetching on every keystroke after insertion.
		// expValEquals('platform_editor_agent_mentions'...) intentionally omitted — static
		// gate value, not reactive. Inlined in the guard above per lint rules.
		setAgentMentionState,
		setResolvedAgentMentionId,
	]);
};
